import React from 'react';
import { Users, TrendingUp, Activity, BarChart3 } from 'lucide-react';

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'emerald' }) => {
  const colors = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-500' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', icon: 'text-rose-500' },
  };
  const c = colors[color] || colors.emerald;

  return (
    <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-4 sm:p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-stone-500 text-xs sm:text-sm font-medium">{title}</p>
          <p
            className="text-2xl sm:text-3xl font-medium mt-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {value}
          </p>
          {subtitle && (
            <p className="text-stone-400 text-xs mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${c.icon}`} />
        </div>
      </div>
    </div>
  );
};

const PhaseChart = ({ phaseDistribution }) => {
  const phases = [
    { name: 'Stabilize', key: 'phase_1', color: '#8DA399' },
    { name: 'Understand', key: 'phase_2', color: '#9F97C1' },
    { name: 'Reclaim', key: 'phase_3', color: '#C0856B' },
    { name: 'Renew', key: 'phase_4', color: '#D4A373' },
  ];

  const total = phases.reduce((sum, p) => sum + (phaseDistribution?.[p.key] || 0), 0);

  return (
    <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-4 sm:p-6">
      <h3
        className="text-lg font-medium mb-4"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        Phase Distribution
      </h3>
      <div className="space-y-3">
        {phases.map((phase) => {
          const count = phaseDistribution?.[phase.key] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          return (
            <div key={phase.key}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-stone-600">{phase.name}</span>
                <span className="text-stone-400">{count} users</span>
              </div>
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: phase.color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MetricsView = ({ metrics }) => {
  if (!metrics) {
    return (
      <div className="text-center py-12">
        <p className="text-stone-500">Loading metrics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <h2
        className="text-2xl sm:text-3xl font-medium"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        Key Metrics
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          title="Total Users"
          value={metrics.total_users}
          icon={Users}
          color="emerald"
        />
        <MetricCard
          title="Active Subscribers"
          value={metrics.active_subscribers}
          subtitle="Paid + Trial"
          icon={TrendingUp}
          color="blue"
        />
        <MetricCard
          title="Avg. Streak"
          value={`${metrics.average_streak} days`}
          icon={Activity}
          color="amber"
        />
        <MetricCard
          title="Daily Active"
          value={metrics.daily_active_users}
          subtitle="Last 24 hours"
          icon={BarChart3}
          color="rose"
        />
      </div>

      <PhaseChart phaseDistribution={metrics.phase_distribution} />
    </div>
  );
};
